import { Box, IconButton, Stack, Typography, Paper, Divider } from '@mui/material';
import EditDetailsPanel from './edit-details-panel';
import { FlowSettingsTab, FlowContributorsTab, FlowConfigurationTab } from './flow-detail-tabs';
import { FlowControlOutputPreview } from './flow-detail-tabs/flow-control-output-preview';
import { FlowControlNowNextStack } from './flow-detail-tabs/flow-control-now-next-stack';
import { FlowControlSlotMediaBelowRow } from './slot-list/flow-control-slot-media-below-row';
import { PerformLowerControls } from './edit-details/perform-view/perform-lower-controls';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { FlowControlLiveStateProvider } from './flow-control-live-state';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import type { FlowControlPerformMode } from '@/types/player/simulation-cue-sheet.types';
import type { FlowControlNarrowViewMode } from '@/types/player/flow-control-narrow-view.types';
import SlotAssetsDetails from './edit-details/slot-assets-details';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useFlowControlNarrowViewStore } from '@/state/stores/use-flow-control-narrow-view-store';
import {
  findFlowSlotForControlExpandId,
  resolveFlowControlActiveExpandId,
} from '@/helpers/flow-control-playing-segment.helper';
import { resolveFlowControlExpandSegmentMeta } from '@/helpers/flow-control-expand-segment-meta.helper';
import { FlowControlOverlaySegmentHeader } from './slot-list/flow-control-overlay-segment-header';
import { isFlowControlFlowLevelMediaExpandId } from './slot-list/flow-slot-assets-inline-ids';
import { useFlowControlPlayingSegmentExpandId } from './slot-list/use-flow-control-playing-segment-expand-id';
import { useFlowControlLiveToSimulationReset } from './slot-list/use-flow-control-live-to-simulation-reset';
import { useTranslations } from '@/i18n/use-translations';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDetailMode } from './flow-detail.types';
import { useCallback, useEffect, useMemo, useState, startTransition } from 'react';
import { useSearchParams } from 'react-router';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import CaretUp from '@/assets/svg/caret-up';
import CaretDown from '@/assets/svg/caret-down';
import { useFlowControlSegmentBrowse } from './slot-list/use-flow-control-segment-browse';
import { useFlowControlOverlayHeader } from './slot-list/use-flow-control-overlay-header';

// Helper function to check if slot content is available
const isSlotContentAvailable = (slot: FlowSlotDto) => {
  return slot.contentType === 'Session' ? !!slot.content?.title : !!slot.title;
};

function FlowControlOverlayContent({
  slot,
  slotTitle,
  eventSlotId,
  onCloseOverlay,
}: {
  slot?: FlowSlotDto;
  slotTitle?: string;
  eventSlotId?: string;
  onCloseOverlay: () => void;
}) {
  const t = useTranslations();
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const expandId = useFlowControlMediaExpandStore((s) => s.expandId);
  const expandSource = useFlowControlMediaExpandStore((s) => s.expandSource);
  const playingExpandId = useFlowControlPlayingSegmentExpandId();
  const narrowViewMode = useFlowControlNarrowViewStore((s) => s.mode);
  const setNarrowViewMode = useFlowControlNarrowViewStore((s) => s.setMode);
  const planFlowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow) : undefined;
  const performMode: FlowControlPerformMode = flow?.liveContext?.isRunning ? 'play' : 'simulation';
  useFlowControlLiveToSimulationReset(flow ?? undefined, planFlowId, performMode);
  const entityRef = useMemo(() => {
    if (performMode === 'play') {
      const id = bindingFlowId ?? planFlowId;
      return id ? getEntityRefFromFlow(id, flow?.liveContext) : undefined;
    }
    return planFlowId ? `Flow:${planFlowId}` : undefined;
  }, [bindingFlowId, flow?.liveContext, performMode, planFlowId]);

  const { data: liveContextState, dataUpdatedAt: liveContextQueryUpdatedAt } = useGetLiveContextStateQuery(entityRef, {
    enabled: !!entityRef && performMode === 'play',
  });

  const activeExpandId = resolveFlowControlActiveExpandId(expandId, playingExpandId, slot?.id, expandSource);
  const resolvedSlot = useMemo(() => {
    if (!activeExpandId) {
      return slot ?? null;
    }
    return findFlowSlotForControlExpandId(flowPlan?.slots, activeExpandId) ?? slot ?? null;
  }, [activeExpandId, flowPlan?.slots, slot]);

  const segmentMeta = useMemo(() => {
    if (!activeExpandId) {
      return null;
    }
    return resolveFlowControlExpandSegmentMeta(activeExpandId, resolvedSlot, t, flowPlan?.slots);
  }, [activeExpandId, resolvedSlot, t, flowPlan?.slots]);

  const mediaBelowSlot = activeExpandId && isFlowControlFlowLevelMediaExpandId(activeExpandId) ? null : resolvedSlot;

  const handleNarrowViewModeChange = useCallback(
    (mode: FlowControlNarrowViewMode) => {
      setNarrowViewMode(mode);
      if (mode === 'list') {
        onCloseOverlay();
      }
    },
    [onCloseOverlay, setNarrowViewMode],
  );

  if (!flow || !entityRef) {
    return null;
  }

  const isProgramAndPreview = narrowViewMode === 'programAndPreview';

  return (
    <FlowControlLiveStateProvider entityRef={entityRef} performMode={performMode}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {isProgramAndPreview ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: 1,
                pt: 1,
                pb: 0.5,
              }}
            >
              <FlowControlNowNextStack
                flow={flow}
                performMode={performMode}
                liveContextState={liveContextState}
                planFlowId={planFlowId}
                entityRef={entityRef}
                liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
              />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  flex: '1 1 0',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  px: 1,
                  pt: 1,
                  pb: 0.5,
                }}
              >
                <FlowControlOutputPreview
                  flow={flow}
                  performMode={performMode}
                  liveContextState={liveContextState}
                  planFlowId={planFlowId}
                />
              </Box>
              <Divider />
              <Box
                sx={{
                  flex: '1 1 0',
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  px: { xs: 1, sm: 2 },
                  pt: 0.5,
                }}
              >
                {activeExpandId && segmentMeta ? (
                  <>
                    <Box sx={{ flexShrink: 0, pb: 0.5 }}>
                      <FlowControlOverlaySegmentHeader
                        meta={segmentMeta}
                        isActive={playingExpandId === activeExpandId}
                      />
                    </Box>
                    <FlowControlSlotMediaBelowRow
                      slot={mediaBelowSlot}
                      activeSlotId={activeExpandId}
                      show
                      slotTitle={slotTitle}
                      scrollableList
                    />
                  </>
                ) : activeExpandId ? (
                  <FlowControlSlotMediaBelowRow
                    slot={mediaBelowSlot}
                    activeSlotId={activeExpandId}
                    show
                    slotTitle={slotTitle}
                    scrollableList
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Select a segment in the schedule to browse and stage media.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <PerformLowerControls
            flow={flow}
            entityRef={entityRef}
            eventSlotId={eventSlotId}
            performMode={performMode}
            liveContextState={liveContextState}
            liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
            stickyFooter={false}
            flushEdges
            showNarrowViewModes
            narrowViewMode={narrowViewMode === 'list' ? 'programWithList' : narrowViewMode}
            onNarrowViewModeChange={handleNarrowViewModeChange}
          />
        </Box>
      </Box>
    </FlowControlLiveStateProvider>
  );
}

interface EditDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  slot?: FlowSlotDto;
  mode?: FlowDetailMode;
  onNavigateToFlowDetails?: (direction: 'up' | 'down') => void;
  cameFromDirectionProp?: 'up' | 'down';
  onSlotChange?: (slot: FlowSlotDto) => void;
  onSlotDeleted?: () => void;
  eventSlotIdParam?: string | null;
  /** Props for FlowControlTab when mode is flowControl */
  setShowFlowDetails?: React.Dispatch<React.SetStateAction<FlowDetailMode | undefined>>;
  setFlowDetailsDirection?: React.Dispatch<React.SetStateAction<'up' | 'down' | undefined>>;
  onShowTimingAlert?: () => void;
  onShowPublishAlert?: () => void;
  onSlotDeletedForControl?: () => void;
}

/* eslint-disable @typescript-eslint/no-unused-vars -- setShowFlowDetails, setFlowDetailsDirection, onShowTimingAlert, onShowPublishAlert, onSlotDeletedForControl reserved for FlowControlTab */
export default function EditDetailsDialog({
  open,
  onClose,
  title,
  subtitle,
  slot,
  mode,
  onNavigateToFlowDetails,
  cameFromDirectionProp,
  onSlotChange,
  onSlotDeleted,
  eventSlotIdParam,
  setShowFlowDetails: _setShowFlowDetails,
  setFlowDetailsDirection: _setFlowDetailsDirection,
  onShowTimingAlert: _onShowTimingAlert,
  onShowPublishAlert: _onShowPublishAlert,
  onSlotDeletedForControl: _onSlotDeletedForControl,
}: EditDetailsDialogProps) {
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const flow = useFlowStore((s) => s.flow);
  const [cameFromDirection, setCameFromDirection] = useState<'up' | 'down' | undefined>(undefined);
  const [, setSearchParams] = useSearchParams();
  const { hasAccess: hasPerformFeatureAccess, isLoading: isPerformFeatureAccessLoading } = useFeatureAccess('perform');
  const performTabsAllowed = !isPerformFeatureAccessLoading && hasPerformFeatureAccess;
  const { stepSegment, canStepUp, canStepDown } = useFlowControlSegmentBrowse();
  const narrowViewMode = useFlowControlNarrowViewStore((s) => s.mode);
  const isFlowControlBrowseMode = mode === 'flowControl';
  /** Now/Next stack already labels On Air / Simulation & Next — skip segment browse chrome. */
  const hideOverlayHeader = isFlowControlBrowseMode && narrowViewMode === 'programAndPreview';
  const flowControlOverlayHeader = useFlowControlOverlayHeader(slot);
  const headerTitle = isFlowControlBrowseMode ? flowControlOverlayHeader.title : title;
  const headerSubtitle = isFlowControlBrowseMode ? flowControlOverlayHeader.subtitle : subtitle;

  useEffect(() => {
    if (!open || isPerformFeatureAccessLoading) return;
    if ((mode === 'flowConfiguration' || mode === 'flowControl') && !hasPerformFeatureAccess) {
      onClose();
    }
  }, [open, isPerformFeatureAccessLoading, hasPerformFeatureAccess, mode, onClose]);

  const setUrlForSlot = (slot: FlowSlotDto) => {
    const params = new URLSearchParams();
    params.set('view', 'slot');
    params.set('slotId', slot.id);
    setSearchParams(params, { replace: true });
  };

  // Sync derived state when props change
  useEffect(() => {
    startTransition(() => {
      if (!slot && cameFromDirectionProp) {
        setCameFromDirection(cameFromDirectionProp);
      } else if (slot) {
        setCameFromDirection(undefined);
      }
    });
  }, [slot, cameFromDirectionProp]);

  const { prevSlot, nextSlot, isFirstSlot, isLastSlot, showUpArrow, showDownArrow } = useMemo(() => {
    // Filter slots to only include those with available content
    const availableSlots = flow?.slots?.filter(isSlotContentAvailable) ?? [];

    // If we're in default view (no slot selected)
    if (!slot) {
      return {
        prevSlot: undefined,
        nextSlot: undefined,
        isFirstSlot: false,
        isLastSlot: false,
        // If came from up (first slot), show down arrow to go back down
        // If came from down (last slot), show up arrow to go back up
        showUpArrow: cameFromDirection === 'down',
        showDownArrow: cameFromDirection === 'up',
      };
    }

    // Check if current slot has available content
    if (!isSlotContentAvailable(slot)) {
      // For slots with unavailable content, find next/previous available slots
      // based on position in the original slot list
      const currentSlotIndex = flow?.slots?.findIndex((s) => s.id === slot?.id) ?? -1;
      if (currentSlotIndex === -1) {
        return {
          prevSlot: undefined,
          nextSlot: undefined,
          isFirstSlot: false,
          isLastSlot: false,
          showUpArrow: false,
          showDownArrow: false,
        };
      }

      // Find previous available slot
      let prevSlot: FlowSlotDto | undefined;
      for (let i = currentSlotIndex - 1; i >= 0; i--) {
        const slot = flow?.slots?.[i];
        if (slot && isSlotContentAvailable(slot)) {
          prevSlot = slot;
          break;
        }
      }

      // Find next available slot
      let nextSlot: FlowSlotDto | undefined;
      for (let i = currentSlotIndex + 1; i < (flow?.slots?.length ?? 0); i++) {
        const slot = flow?.slots?.[i];
        if (slot && isSlotContentAvailable(slot)) {
          nextSlot = slot;
          break;
        }
      }

      // Check if this is the first/last available slot
      const isFirstAvailableSlot = !prevSlot;
      const isLastAvailableSlot = !nextSlot;

      return {
        prevSlot,
        nextSlot,
        isFirstSlot: isFirstAvailableSlot,
        isLastSlot: isLastAvailableSlot,
        showUpArrow: true, // Always show arrows for unavailable content slots
        showDownArrow: true,
      };
    }

    const currentSlotIndex = availableSlots.findIndex((s) => s.id === slot?.id);
    if (currentSlotIndex === undefined || currentSlotIndex === -1) {
      return {
        prevSlot: undefined,
        nextSlot: undefined,
        isFirstSlot: false,
        isLastSlot: false,
        showUpArrow: false,
        showDownArrow: false,
      };
    }

    const prevSlot = availableSlots[currentSlotIndex - 1];
    const nextSlot = availableSlots[currentSlotIndex + 1];
    const isFirstSlot = currentSlotIndex === 0;
    const isLastSlot = currentSlotIndex === availableSlots.length - 1;

    return {
      prevSlot,
      nextSlot,
      isFirstSlot,
      isLastSlot,
      showUpArrow: true, // Always show up arrow when viewing a slot
      showDownArrow: true, // Always show down arrow when viewing a slot
    };
  }, [slot, flow?.slots, cameFromDirection]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        // Sit below FixedNavigationBar (48px at 50/64) + FlowBar (52px at 98/112).
        top: { xs: '150px', md: '164px' },
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!hideOverlayHeader && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            pr: 1,
            minHeight: 42,
            flexShrink: 0,
          }}
        >
          <Stack
            justifyContent="center"
            alignItems="flex-start"
            spacing={0}
            sx={{ flex: 1, py: 1, pl: 1.5, minWidth: 0 }}
          >
            {headerTitle && (
              <Typography
                variant="h6"
                component="h1"
                align="center"
                sx={{
                  pr: 1,
                  textAlign: 'left',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  maxHeight: '3em', // ~2 lines for h6
                  lineHeight: headerSubtitle !== undefined ? 1.2 : 1.4,
                }}
              >
                {headerTitle}
              </Typography>
            )}
            {headerSubtitle !== undefined && (
              <Typography
                variant="subtitle1"
                color="textDisabled"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {headerSubtitle}
              </Typography>
            )}
          </Stack>
          <Box sx={{ width: 65, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isFlowControlBrowseMode) {
                    stepSegment(-1);
                    return;
                  }
                  if (!slot && mode && cameFromDirection === 'down') {
                    // In default view (flow details), came from down (last slot), up arrow takes us back to last available slot
                    const availableSlots = flow?.slots?.filter(isSlotContentAvailable) ?? [];
                    const lastSlot = availableSlots[availableSlots.length - 1];
                    if (lastSlot) {
                      setUrlForSlot(lastSlot);
                      onSlotChange?.(lastSlot);
                    }
                  } else if (prevSlot) {
                    // Normal navigation to previous slot
                    setUrlForSlot(prevSlot);
                    onSlotChange?.(prevSlot);
                  } else if (isFirstSlot) {
                    // At first slot, going up opens flow-level slot assets (moderators + flow media)
                    onNavigateToFlowDetails?.('up');
                  }
                }}
                sx={{ visibility: (isFlowControlBrowseMode ? canStepUp : showUpArrow) ? 'visible' : 'hidden' }}
              >
                <Box sx={{ height: 24, width: 24 }}>
                  <CaretUp />
                </Box>
              </IconButton>
              <IconButton
                size="small"
                onClick={() => {
                  if (isFlowControlBrowseMode) {
                    stepSegment(1);
                    return;
                  }
                  if (!slot && mode && cameFromDirection === 'up') {
                    // In default view (flow details), came from up (first slot), down arrow takes us back to first available slot
                    const availableSlots = flow?.slots?.filter(isSlotContentAvailable) ?? [];
                    const firstSlot = availableSlots[0];
                    if (firstSlot) {
                      setUrlForSlot(firstSlot);
                      onSlotChange?.(firstSlot);
                    }
                  } else if (!slot && mode && !cameFromDirection) {
                    // In flow settings, down arrow goes to first available slot
                    const availableSlots = flow?.slots?.filter(isSlotContentAvailable) ?? [];
                    const firstSlot = availableSlots[0];
                    if (firstSlot) {
                      setUrlForSlot(firstSlot);
                      onSlotChange?.(firstSlot);
                    }
                  } else if (nextSlot) {
                    // Normal navigation to next slot
                    setUrlForSlot(nextSlot);
                    onSlotChange?.(nextSlot);
                  } else if (isLastSlot) {
                    // At last slot, going down opens flow-level slot assets (moderators + flow media)
                    onNavigateToFlowDetails?.('down');
                  }
                }}
                sx={{ visibility: (isFlowControlBrowseMode ? canStepDown : showDownArrow) ? 'visible' : 'hidden' }}
              >
                <Box sx={{ height: 24, width: 24 }}>
                  <CaretDown />
                </Box>
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: mode === 'flowControl' ? 'hidden' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: mode === 'flowControl' ? 0 : 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{ flex: 1, minHeight: 0, height: '100%', p: 0, display: 'flex', flexDirection: 'column' }}
        >
          {mode === 'flowControl' && performTabsAllowed ? (
            <FlowControlOverlayContent
              slot={slot}
              slotTitle={title}
              eventSlotId={eventSlotIdParam ?? undefined}
              onCloseOverlay={onClose}
            />
          ) : mode === 'flowSlotAssets' ? (
            <SlotAssetsDetails selectedSlot={slot} slotTitle={title} />
          ) : slot || mode === 'flowSchedule' ? (
            <EditDetailsPanel
              slot={slot}
              slotTitle={title}
              onSlotDeleted={onSlotDeleted}
              eventSlotIdParam={eventSlotIdParam}
            />
          ) : mode === 'flowSettings' ? (
            <FlowSettingsTab />
          ) : mode === 'flowContributors' ? (
            <FlowContributorsTab />
          ) : mode === 'flowConfiguration' && performTabsAllowed ? (
            <FlowConfigurationTab />
          ) : null}
        </Paper>
      </Box>
    </Box>
  );
}
